import { memo, useCallback, useState, useEffect, useMemo } from 'react';
import { cn, Collapse } from '@divlab/divanui';
import { IconMinus, IconPlusBold } from '@divlab/divanui/icons';

import styles from './MattressesLayersAccordion.module.css';

import type { MattressesLayerData } from '@Types/Mattresses';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

export interface MattressesLayersAccordionProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  layers: MattressesLayerData[];
  activeLayer?: number;
  onToggleLayer?: (e: MouseEvent, indexes: number[]) => void;
}

const iconProps = { className: styles.icon };

const MattressesLayersAccordion: FC<MattressesLayersAccordionProps> = (props) => {
  const { className, layers, activeLayer, onToggleLayer, ...restProps } = props;
  const [openedLayer, setOpenedLayer] = useState<MattressesLayerData>(layers[activeLayer]);

  // Массив уникальных слоёв
  const uniqLayers = useMemo(() => {
    const hashmap = {};

    return layers.filter((layer) => {
      if (hashmap[layer.title]) return false;
      hashmap[layer.title] = 1;
      return true;
    });
  }, [layers]);

  const handleClickLayer = useCallback(
    (e: MouseEvent, layer: MattressesLayerData) => {
      const indexes = [];
      const newLayer = openedLayer === layer ? null : layer;
      let timeout = 0;

      layers.forEach((l, index) => {
        if (l.title !== layer.title) return;
        indexes.push(index);
      });

      if (openedLayer) {
        setOpenedLayer(null);
        timeout = 400;

        if (onToggleLayer) onToggleLayer(e, []);
      }

      if (newLayer) {
        setTimeout(() => {
          setOpenedLayer(newLayer);
          if (indexes.length && onToggleLayer) {
            onToggleLayer(e, indexes);
          }
        }, timeout);
      }
    },
    [layers, onToggleLayer, openedLayer],
  );

  useEffect(() => {
    setOpenedLayer(layers[activeLayer]);
  }, [activeLayer, layers]);

  return (
    <div {...restProps} className={cn(styles.accordion, className)}>
      {uniqLayers.map((layer, index) => {
        const collapsed = !openedLayer || openedLayer.title !== layer.title;

        return (
          <div className={styles.item} key={index} onClick={(e) => handleClickLayer(e, layer)}>
            <div className={styles.wrapperTitle}>
              <div className={styles.title}>{layer.title}</div>

              <div className={styles.wrapperIcon}>
                {collapsed ? <IconPlusBold {...iconProps} /> : <IconMinus {...iconProps} />}
              </div>
            </div>

            <Collapse collapsed={collapsed} duration={400}>
              <div className={styles.description}>{layer.description}</div>
            </Collapse>
          </div>
        );
      })}
    </div>
  );
};

export default memo(MattressesLayersAccordion);
